import { useRef } from 'react';
import { AttachFilesControl } from '@kula-ai/components';
import {
  EMPTY_SELECTION,
  fromSelectedValues,
  toAttachOptions,
  toSelectedValues,
} from './attachDocumentSelection';
import type { AttachmentSelection } from './attachDocumentSelection';
import type { InternalEmailDocuments } from '@/api/internalEmails';

export interface AttachDocumentsControlProps {
  documents?: InternalEmailDocuments;
  value?: AttachmentSelection;
  onChange?: (_selection: AttachmentSelection) => void;
}

/**
 * Picks which of the candidate's documents to attach, from inside the editor
 * toolbar.
 *
 * The picker itself is kula's `AttachFilesControl`; this only translates. It
 * lists document *names* — the files are never loaded here, and the sender's
 * picks travel as references resolved when the email is sent. Only "Upload new
 * document" introduces real bytes, which is why the file input lives on this
 * side: the control raises `onUpload` and the host owns the picker, so file
 * validation stays in one place.
 */
export const AttachDocumentsControl = ({
  documents,
  value = EMPTY_SELECTION,
  onChange = () => {},
}: AttachDocumentsControlProps) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleUploaded = (files: FileList | null) => {
    if (!files?.length) return;
    onChange({ ...value, uploads: [...value.uploads, ...Array.from(files)] });
  };

  return (
    <>
      <AttachFilesControl
        options={toAttachOptions(documents)}
        value={toSelectedValues(value)}
        onChange={(values) => onChange(fromSelectedValues(values, value))}
        // Uploads are not rows in the list, so the badge would under-report the
        // attachment count without them.
        extraCount={value.uploads.length}
        onUpload={() => fileInputRef.current?.click()}
      />
      <input
        ref={fileInputRef}
        type="file"
        multiple
        hidden
        aria-hidden="true"
        onChange={(event) => {
          handleUploaded(event.target.files);
          // Let the same file be chosen again after it is removed.
          event.target.value = '';
        }}
        data-test-id="attach-documents-file-input"
      />
    </>
  );
};
